import { useState } from "react";
import { Phone, Mail, MapPin, Instagram, Facebook, Loader2, CheckCircle2, Send } from "lucide-react";
import { api, formatApiError, SITE } from "../lib/api";

const EVENT_TYPES = ["Mariage", "Anniversaire", "Soirée privée", "Festival", "Nouvel an", "Entreprise / Afterwork", "Baptême", "Cousinade", "Comité des fêtes", "Soirée étudiante", "Autre"];

export default function Contact() {
  const [form, setForm] = useState({
    name: "", phone: "", email: "", event_type: "Mariage", event_date: "", guest_count: "", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await api.post("/contact", form);
      setSuccess(true);
      setForm({ name: "", phone: "", email: "", event_type: "Mariage", event_date: "", guest_count: "", message: "" });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="relative py-28 lg:py-40 bg-[#070707] overflow-hidden" data-testid="contact-section">
      <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#3B82F6]/10 blur-[120px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-12">
          {/* Left */}
          <div className="lg:col-span-5 reveal">
            <div className="text-xs uppercase tracking-[0.4em] text-[#3B82F6]">Contact</div>
            <h2 className="font-display text-5xl md:text-7xl mt-4 leading-[0.9]">
              Parlons de<br />
              <span className="text-[#3B82F6]">votre soirée.</span>
            </h2>
            <p className="text-zinc-400 mt-6 max-w-md">Devis personnalisé sous 24h. Réponse rapide, conseils sur-mesure et logistique inclus.</p>

            <div className="mt-10 space-y-4">
              <ContactItem
                icon={Phone}
                label="Téléphone"
                value={SITE.phone}
                href={`tel:${SITE.phoneIntl}`}
                testid="contact-phone"
              />
              <ContactItem
                icon={Mail}
                label="Email"
                value={SITE.email}
                href={`mailto:${SITE.email}`}
                testid="contact-email"
              />
              <ContactItem
                icon={MapPin}
                label="Zone d'intervention"
                value="Charente · Charente-Maritime · Deux-Sèvres · Vienne · Limitrophe Charente"
                testid="contact-zones"
              />
            </div>

            <div className="mt-10 flex gap-3">
              <a href={SITE.social.instagram} target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-full border border-white/10 flex items-center justify-center hover:border-[#3B82F6] hover:text-[#3B82F6] transition-all" data-testid="contact-instagram" aria-label="Instagram">
                <Instagram size={18} />
              </a>
              <a href={SITE.social.facebook} target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-full border border-white/10 flex items-center justify-center hover:border-[#3B82F6] hover:text-[#3B82F6] transition-all" data-testid="contact-facebook" aria-label="Facebook">
                <Facebook size={18} />
              </a>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-7 reveal">
            <div className="glass-strong rounded-2xl p-6 md:p-10">
              {success ? (
                <div className="flex flex-col items-center text-center py-12" data-testid="contact-success">
                  <div className="w-16 h-16 rounded-full bg-[#3B82F6]/10 border border-[#3B82F6]/30 flex items-center justify-center text-[#3B82F6] mb-5 animate-pulse-glow">
                    <CheckCircle2 size={26} />
                  </div>
                  <div className="font-display text-3xl">Demande reçue</div>
                  <p className="text-zinc-400 mt-2 max-w-md">Merci ! Anton revient vers vous sous 24h avec un devis personnalisé.</p>
                  <button onClick={() => setSuccess(false)} className="btn-ghost mt-8" data-testid="contact-new-request">
                    Envoyer une autre demande
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="grid md:grid-cols-2 gap-5" data-testid="contact-form">
                  <Field label="Nom" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testid="contact-name" />
                  <Field label="Téléphone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} required testid="contact-phone-input" />
                  <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required testid="contact-email-input" full />

                  <div>
                    <label className="text-[11px] uppercase tracking-[0.3em] text-zinc-400">Type d'événement</label>
                    <select
                      value={form.event_type}
                      onChange={(e) => setForm({ ...form, event_type: e.target.value })}
                      className="mt-2 w-full bg-[#0a0a0a] border border-white/10 rounded-xl px-4 py-3 text-sm focus:border-[#3B82F6] focus:outline-none transition-colors"
                      data-testid="contact-event-type"
                    >
                      {EVENT_TYPES.map((o) => <option key={o}>{o}</option>)}
                    </select>
                  </div>

                  <Field label="Date envisagée" type="text" value={form.event_date} onChange={(v) => setForm({ ...form, event_date: v })} testid="contact-event-date" placeholder="Ex: 14 juillet 2026" />
                  <Field label="Nombre de personnes" value={form.guest_count} onChange={(v) => setForm({ ...form, guest_count: v })} testid="contact-guest-count" full />

                  <div className="md:col-span-2">
                    <label className="text-[11px] uppercase tracking-[0.3em] text-zinc-400">Message</label>
                    <textarea
                      required
                      rows={5}
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      className="mt-2 w-full bg-[#0a0a0a] border border-white/10 rounded-xl px-4 py-3 text-sm focus:border-[#3B82F6] focus:outline-none transition-colors resize-none"
                      placeholder="Décrivez votre événement, le lieu, l'ambiance souhaitée…"
                      data-testid="contact-message"
                    />
                  </div>

                  {error && <div className="md:col-span-2 text-sm text-red-400" data-testid="contact-error">{error}</div>}

                  <div className="md:col-span-2 flex justify-end">
                    <button type="submit" disabled={submitting} className="btn-neon disabled:opacity-50" data-testid="contact-submit">
                      {submitting ? <><Loader2 size={16} className="animate-spin" /> Envoi…</> : <>Demander un devis <Send size={16} /></>}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ContactItem({ icon: Icon, label, value, href, testid }) {
  const inner = (
    <div className="flex items-center gap-4 p-4 rounded-xl border border-white/10 hover:border-[#3B82F6]/50 hover:bg-[#3B82F6]/5 transition-all">
      <div className="w-11 h-11 rounded-xl bg-[#3B82F6]/10 border border-[#3B82F6]/30 text-[#3B82F6] flex items-center justify-center">
        <Icon size={18} />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">{label}</div>
        <div className="text-sm text-zinc-100 mt-0.5">{value}</div>
      </div>
    </div>
  );
  return href ? <a href={href} data-testid={testid}>{inner}</a> : <div data-testid={testid}>{inner}</div>;
}

function Field({ label, value, onChange, type = "text", required, testid, full, placeholder }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="text-[11px] uppercase tracking-[0.3em] text-zinc-400">{label}</label>
      <input
        type={type}
        required={required}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="mt-2 w-full bg-[#0a0a0a] border border-white/10 rounded-xl px-4 py-3 text-sm focus:border-[#3B82F6] focus:outline-none transition-colors"
        data-testid={testid}
      />
    </div>
  );
}
